import React, { useCallback, useMemo } from "react";
import { Camera, Euler, Vector2, Vector2Like } from "three";
import { useRef, useState, useEffect } from "react";
import { useDemoState } from "@/state/demoState";
import { FurnitureInstance } from "@/state/Furniture";
import { FurnitureInstanceRenderer } from "./FurnitureInstanceRenderer";
import { AABB, getModelInstanceAabb } from "@/3d/movement2d/aabb2d";
import {
    filterRoomFractionsBySize,
    fixAabbPositionByRoomFractions,
    getRoomWallRotation,
    moveAABBtoPoint,
    stickAabbToRoom,
} from "@/3d/movement2d/aabb2dIntersections";
import { useFurnitureManagementState } from "@/state/furnitureManagementState";
import { useMouseInteractions } from "@/utils/useMouseInteractions";
import { SELECTED_ITEM_TYPE, SelectedItem } from "@/3d/SelectedItem";

type Props = {
    cameraRef: React.RefObject<Camera>;
    furnitureInstances: Record<string, FurnitureInstance>;
};

const DEBUG_FREE_SPACE = false;

// todo: disable interactions when in 2d view
// todo: refactor
export const FurnitureMouseInteractions: React.FC<Props> = ({ furnitureInstances, cameraRef }) => {
    const updateFreeSpace = useFurnitureManagementState((state) => state.updateFreeSpace);
    const view = useDemoState((state) => state.view);
    const step = useDemoState((state) => state.step);
    const setSelectedItem = useDemoState((state) => state.setSelectedItem);
    const updateFurnitureInstancePosition = useFurnitureManagementState(
        (state) => state.updateFurnitureInstancePosition
    );

    const [furnitureFreeSpace, setFurnitureFreeSpace] = useState<{
        original: AABB[];
        rotated: AABB[];
    }>({ original: [], rotated: [] });

    const [draggingFurnitureId, setDraggingFurnitureId] = useState<string | undefined>(undefined);

    const [furnitureAabbs, setFurnitureAabbs] = useState<Record<string, AABB>>({});
    const furnitureRotationRef = useRef<Record<string, number>>({});

    const setIsDragging = useDemoState((state) => state.setIsDragging);
    const roomConfig = useDemoState((state) => state.roomConfig);

    const roomAabb = useMemo(() => {
        return new AABB(
            -roomConfig.width / 2,
            roomConfig.width / 2,
            -roomConfig.depth / 2,
            roomConfig.depth / 2
        );
    }, [roomConfig]);

    useEffect(() => {
        const fractions = updateFreeSpace(
            roomAabb,
            (instance) => instance.id !== draggingFurnitureId
        );

        if (!draggingFurnitureId) {
            return;
        }

        const hasAabb = furnitureAabbs[draggingFurnitureId] !== undefined;
        const instance = furnitureInstances[draggingFurnitureId];
        const aabb = hasAabb
            ? furnitureAabbs[draggingFurnitureId]
            : getModelInstanceAabb(instance.instance);

        if (!hasAabb) {
            furnitureRotationRef.current[instance.id] = instance.instance.rotation;
        }

        const is90Rotation = checkIs90Rotation(furnitureRotationRef.current[instance.id]);

        const filteredFractions = filterRoomFractionsBySize(fractions, aabb.width, aabb.height);
        const filteredRotatedFractions = filterRoomFractionsBySize(
            fractions,
            aabb.height,
            aabb.width
        );

        setFurnitureFreeSpace({
            original: is90Rotation ? filteredRotatedFractions : filteredFractions,
            rotated: is90Rotation ? filteredFractions : filteredRotatedFractions,
        });
    }, [furnitureInstances, draggingFurnitureId]);

    const handleDrag = useCallback(
        (item: SelectedItem, target: Vector2Like) => {
            if (item.selectedItemType !== SELECTED_ITEM_TYPE.FURNITURE) return;

            const { furnitureInstanceId } = item;
            const furnitureInstance = furnitureInstances[furnitureInstanceId];

            const originAabb = new AABB(
                furnitureInstance.instance.box.min.x,
                furnitureInstance.instance.box.max.x,
                furnitureInstance.instance.box.min.z,
                furnitureInstance.instance.box.max.z
            );

            const existingRotation = furnitureRotationRef.current[furnitureInstanceId] ?? 0;
            const is90Rotation = checkIs90Rotation(existingRotation);

            const rotatedAabb = is90Rotation ? originAabb.rotate90() : originAabb;

            const targetAabb = moveAABBtoPoint(rotatedAabb, new Vector2(target.x, target.y));

            const rotation = getRoomWallRotation(target, roomAabb, 0.1);
            if (rotation !== undefined) {
                furnitureRotationRef.current[furnitureInstanceId] = rotation;
            }
            const isRotated = checkIs90Rotation(furnitureRotationRef.current[furnitureInstanceId]);

            const stickedAabb = stickAabbToRoom(targetAabb, roomAabb, 0.2);

            const fixedAabb = fixAabbPositionByRoomFractions(
                stickedAabb,
                isRotated ? furnitureFreeSpace.rotated : furnitureFreeSpace.original
            );

            setFurnitureAabbs((prev) => ({
                ...prev,
                [furnitureInstanceId]: fixedAabb ?? stickedAabb,
            }));
        },
        [furnitureInstances, roomAabb, furnitureFreeSpace]
    );

    const handleDragStart = useCallback((item: SelectedItem) => {
        if (item.selectedItemType !== SELECTED_ITEM_TYPE.FURNITURE) return false;

        setIsDragging(true);
        setDraggingFurnitureId(item.furnitureInstanceId);

        return true;
    }, []);

    const handleDragEnd = useCallback(() => {
        setIsDragging(false);
        setDraggingFurnitureId(undefined);

        if (!draggingFurnitureId) return;

        const existingRotation = furnitureRotationRef.current[draggingFurnitureId] ?? 0;
        const aabb = furnitureAabbs[draggingFurnitureId];

        updateFurnitureInstancePosition(
            draggingFurnitureId,
            {
                x: aabb.minX,
                y: aabb.minY,
            },
            existingRotation
        );
    }, [draggingFurnitureId, furnitureAabbs, updateFurnitureInstancePosition]);

    const handleClick = useCallback((item: SelectedItem | undefined) => {
        if (item === undefined) {
            setSelectedItem(undefined);
            return;
        }

        if (item.selectedItemType !== SELECTED_ITEM_TYPE.FURNITURE) return;

        setSelectedItem(item);

        return true;
    }, []);

    useMouseInteractions({
        cameraRef,
        onDrag: handleDrag,
        onDragStart: handleDragStart,
        onDragEnd: handleDragEnd,
        onClick: handleClick,
        filter: (item) => item.selectedItemType === SELECTED_ITEM_TYPE.FURNITURE,
        isEnabled: view === "3d" && step === "furniture placement",
    });

    return (
        <group>
            {Object.values(furnitureInstances).map((instance) => {
                const aabb = furnitureAabbs[instance.id] ?? getModelInstanceAabb(instance.instance);
                const rotation =
                    furnitureRotationRef.current[instance.id] ?? instance.instance.rotation ?? 0;
                const euler = new Euler(0, rotation, 0, "XYZ");

                return (
                    <group
                        key={instance.id}
                        position={[aabb.center.x, 0, aabb.center.y]}
                        rotation={euler}
                    >
                        <group
                            position={[
                                -instance.instance.box.sizeX / 2,
                                0,
                                -instance.instance.box.sizeZ / 2,
                            ]}
                        >
                            <FurnitureInstanceRenderer furniture={instance} />
                        </group>
                    </group>
                );
            })}
            {DEBUG_FREE_SPACE &&
                furnitureFreeSpace.rotated.map((aabb, index) => (
                    <group
                        key={index}
                        position={[aabb.center.x, index * 0.1, aabb.center.y]}
                        scale={[aabb.width, 0.1, aabb.height]}
                    >
                        <mesh receiveShadow castShadow>
                            <boxGeometry args={[1, 1, 1]} />
                            <meshBasicMaterial color="green" />
                        </mesh>
                        <mesh receiveShadow castShadow>
                            <boxGeometry args={[1, 1, 1]} />
                            <meshBasicMaterial color="white" wireframe />
                        </mesh>
                    </group>
                ))}
        </group>
    );
};

const checkIs90Rotation = (rotation: number) => {
    return rotation === Math.PI / 2 || rotation === -Math.PI / 2;
};
